// VisitDialog — shared Site Visit create/edit form. Used by SchedulePage (/app/schedule)
// and UnifiedCalendar (project/global/portal calendars' "Add" menu + blank-day quick create).
import { useEffect, useState } from "react";
import { X, Save, Loader2, CalendarDays, Repeat, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const todayIso = () => new Date().toISOString().slice(0, 10);

export function VisitDialog({ open, mode = "create", initial, prefill, clients, buildings, templates, meta, lockedClientId, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [recurMode, setRecurMode] = useState("count"); // count | until
    const [busy, setBusy] = useState(false);
    const [internalUsers, setInternalUsers] = useState([]);

    useEffect(() => {
        setForm(initial || {
            client_id: prefill?.client_id || lockedClientId || "", building_id: "", building_ids: [], visit_type: "Compliance check",
            project_id: prefill?.project_id || "", project_label: prefill?.project_label || "",
            title: "", assignee_id: "", scheduled_date: prefill?.scheduled_date || todayIso(), scheduled_time: "",
            recurrence: "None", recurrence_count: 6, recurrence_until: "", template_id: "",
            auto_create_report: true, notes: "", status: "Scheduled",
        });
        setRecurMode("count");
    }, [initial, open, prefill, lockedClientId]);

    useEffect(() => {
        if (open) api.get("/visits/assignable-users").then((r) => setInternalUsers(r.data)).catch(() => setInternalUsers([]));
    }, [open]);

    if (!open) return null;
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const filteredBuildings = (buildings || []).filter((b) => !form.client_id || b.client_id === form.client_id);
    const toggleBuilding = (id) => setForm((f) => {
        const cur = f.building_ids || [];
        return { ...f, building_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
    });

    async function save() {
        if (!form.client_id) { toast.error("Client is required"); return; }
        if (!form.scheduled_date) { toast.error("Scheduled date is required"); return; }
        if (mode === "create" && form.recurrence !== "None" && recurMode === "until" && !form.recurrence_until) {
            toast.error("Choose an end date for the recurrence, or switch to 'Repeat N times'");
            return;
        }
        setBusy(true);
        try {
            if (mode === "create") {
                const payload = {
                    client_id: form.client_id,
                    building_id: (form.building_ids || []).length ? undefined : (form.building_id || undefined),
                    building_ids: (form.building_ids || []).length ? form.building_ids : undefined,
                    project_id: form.project_id || undefined,
                    visit_type: form.visit_type, title: form.title || undefined,
                    assignee_id: form.assignee_id || undefined, scheduled_date: form.scheduled_date,
                    scheduled_time: form.scheduled_time || undefined, recurrence: form.recurrence,
                    template_id: form.template_id || undefined,
                    auto_create_report: form.template_id ? !!form.auto_create_report : undefined,
                    notes: form.notes || undefined,
                };
                if (form.recurrence !== "None") {
                    if (recurMode === "count") payload.recurrence_count = Number(form.recurrence_count) || 6;
                    else if (form.recurrence_until) payload.recurrence_until = form.recurrence_until;
                }
                await api.post("/visits", payload);
                const n = (form.building_ids || []).length;
                toast.success(n > 1 ? `${n} series scheduled (one per building)` : form.recurrence !== "None" ? "Recurring visits scheduled" : "Visit scheduled");
            } else {
                const payload = {
                    visit_type: form.visit_type, title: form.title || undefined,
                    assignee_id: form.assignee_id || undefined, scheduled_date: form.scheduled_date,
                    scheduled_time: form.scheduled_time || undefined, status: form.status,
                    building_id: form.building_id || undefined, template_id: form.template_id || undefined,
                    notes: form.notes || undefined,
                };
                await api.patch(`/visits/${initial.id}`, payload, initial.series_id ? { params: { apply_to_series: true } } : undefined);
                toast.success(initial.series_id ? "Visit updated (applied to this + future occurrences)" : "Visit updated");
            }
            onSaved?.(); onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    }

    async function deleteSeries() {
        if (!initial?.series_id) return;
        if (!window.confirm("Delete this visit and all future occurrences in the series?")) return;
        setBusy(true);
        try {
            await api.delete(`/visits/${initial.id}`, { params: { series: true } });
            toast.success("Series deleted");
            onSaved?.(); onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        } finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="visit-form-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[620px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="w-10 h-10 rounded-[10px] grid place-items-center" style={{ background: "#FBEFE5", color: "#9F4212" }}><CalendarDays className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-[19px] font-bold text-[#1F1F1F]">{mode === "create" ? "Schedule visit" : `Edit visit`}</h2>
                        <p className="text-[12px] mt-0.5" style={{ color: "#6F6A64" }}>{mode === "create" ? "Schedule a one-off or recurring compliance visit." : "Edit this occurrence."}</p>
                    </div>
                    <button data-testid="visit-form-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>

                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4 form-pane">
                    {form.project_id && (
                        <div data-testid="visit-project-link" className="flex items-center gap-2 text-[11.5px] font-semibold px-3 py-2 rounded-lg" style={{ background: "#FBEFE5", color: "#9F4212" }}>
                            <Repeat className="w-3.5 h-3.5" /> Linked to project{form.project_label ? `: ${form.project_label}` : ""}
                        </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Client" required>
                            <select data-testid="visit-form-client" value={form.client_id || ""} disabled={mode === "edit" || !!lockedClientId || !!form.project_id}
                                onChange={(e) => set("client_id", e.target.value)} className="fi">
                                <option value="">Select…</option>
                                {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label={mode === "create" ? "Building(s) (optional)" : "Building (optional)"}>
                            {mode === "create" ? (
                                <div data-testid="visit-form-buildings-multi" className="border rounded-lg px-2.5 py-2 space-y-1 max-h-[110px] overflow-y-auto" style={{ borderColor: "#E5E0DA" }}>
                                    {filteredBuildings.length === 0 && <p className="text-[11.5px] text-[#9A968F] italic">No buildings for this client.</p>}
                                    {filteredBuildings.map((b) => (
                                        <label key={b.id} className="flex items-center gap-2 text-[12px] text-[#2D2D2D]">
                                            <input type="checkbox" data-testid={`visit-form-building-${b.id}`}
                                                checked={(form.building_ids || []).includes(b.id)} onChange={() => toggleBuilding(b.id)} />
                                            {b.name}
                                        </label>
                                    ))}
                                    {(form.building_ids || []).length > 1 && (
                                        <p className="text-[10.5px] text-[var(--brand-accent,_#C75A1B)] font-semibold">Creates one independent series per building selected.</p>
                                    )}
                                </div>
                            ) : (
                                <select data-testid="visit-form-building" value={form.building_id || ""} onChange={(e) => set("building_id", e.target.value)} className="fi">
                                    <option value="">— None —</option>
                                    {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                            )}
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Visit type" required>
                            <select data-testid="visit-form-type" value={form.visit_type || ""} onChange={(e) => set("visit_type", e.target.value)} className="fi">
                                {(meta?.visit_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Assignee">
                            <select data-testid="visit-form-assignee" value={form.assignee_id || ""} onChange={(e) => set("assignee_id", e.target.value)} className="fi">
                                <option value="">— Unassigned —</option>
                                {internalUsers.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                            </select>
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Scheduled date" required>
                            <input data-testid="visit-form-date" type="date" value={form.scheduled_date || ""} onChange={(e) => set("scheduled_date", e.target.value)} className="fi" />
                        </Field>
                        <Field label="Time (optional)">
                            <input data-testid="visit-form-time" type="time" value={form.scheduled_time || ""} onChange={(e) => set("scheduled_time", e.target.value)} className="fi" />
                        </Field>
                    </div>
                    <Field label="Title (auto if blank)">
                        <input data-testid="visit-form-title" value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="fi" />
                    </Field>
                    <Field label="Linked inspection template (optional)">
                        <select data-testid="visit-form-template" value={form.template_id || ""} onChange={(e) => set("template_id", e.target.value)} className="fi">
                            <option value="">— None —</option>
                            {(templates || []).map((t) => <option key={t.id} value={t.id}>{`${t.name} · ${t.code}`}</option>)}
                        </select>
                    </Field>
                    {form.template_id && (
                        <label className="flex items-start gap-2 text-[12.5px] text-[#1F1F1F]">
                            <input type="checkbox" data-testid="visit-form-auto-create" className="mt-0.5"
                                checked={!!form.auto_create_report} onChange={(e) => set("auto_create_report", e.target.checked)} />
                            <span>
                                Auto-create the report
                                <span className="block text-[11px] text-[#9A968F] mt-0.5">A Draft is created automatically each morning of the occurrence and the assignee is notified by email.</span>
                            </span>
                        </label>
                    )}

                    {mode === "create" && (
                        <div className="border rounded-[10px] p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                            <div className="flex items-center gap-1.5 mb-2 text-[12px] font-bold text-[#1F1F1F]"><Repeat className="w-3.5 h-3.5" /> Recurrence</div>
                            <select data-testid="visit-form-recurrence" value={form.recurrence || "None"} onChange={(e) => set("recurrence", e.target.value)} className="fi">
                                {(meta?.recurrence_options || []).map((r) => <option key={r} value={r}>{r === "None" ? "Does not repeat" : r}</option>)}
                            </select>
                            {form.recurrence !== "None" && (
                                <div className="mt-3 space-y-2">
                                    <label className="flex items-center gap-2 text-[12px] text-[#2D2D2D]">
                                        <input type="radio" data-testid="visit-recur-mode-count" checked={recurMode === "count"} onChange={() => setRecurMode("count")} />
                                        Repeat
                                        <input data-testid="visit-form-recur-count" type="number" min={1} max={104} disabled={recurMode !== "count"}
                                            value={form.recurrence_count ?? 6} onChange={(e) => set("recurrence_count", e.target.value)}
                                            className="w-[64px] px-2 py-1 border rounded text-[12px]" style={{ borderColor: "#E5E0DA" }} />
                                        times
                                    </label>
                                    <label className="flex items-center gap-2 text-[12px] text-[#2D2D2D]">
                                        <input type="radio" data-testid="visit-recur-mode-until" checked={recurMode === "until"} onChange={() => setRecurMode("until")} />
                                        Until
                                        <input data-testid="visit-form-recur-until" type="date" disabled={recurMode !== "until"}
                                            value={form.recurrence_until || ""} onChange={(e) => set("recurrence_until", e.target.value)}
                                            className="px-2 py-1 border rounded text-[12px]" style={{ borderColor: "#E5E0DA" }} />
                                    </label>
                                </div>
                            )}
                        </div>
                    )}

                    {mode === "edit" && (
                        <Field label="Status">
                            <select data-testid="visit-form-status" value={form.status || "Scheduled"} onChange={(e) => set("status", e.target.value)} className="fi">
                                {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                    )}
                    <Field label="Notes"><textarea data-testid="visit-form-notes" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="fi" /></Field>
                </div>

                <div className="px-6 py-4 border-t flex items-center gap-3" style={{ borderColor: "#F0EDE8" }}>
                    {mode === "edit" && initial?.series_id && (
                        <button onClick={deleteSeries} disabled={busy} data-testid="visit-form-delete-series"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12.5px] font-semibold text-[#B33A2E] hover:bg-[#FBE8E5]"><Trash2 className="w-3.5 h-3.5" /> Delete series</button>
                    )}
                    <div className="flex-1" />
                    <button onClick={onClose} disabled={busy} data-testid="visit-form-cancel" className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="visit-form-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save"}
                    </button>
                </div>
                <style>{`
                    .form-pane .fi { width: 100%; padding: 8px 11px; background: #fff; border: 1px solid #E5E0DA; border-radius: 8px; font-size: 12.5px; color: #1F1F1F; outline: none; transition: border-color .12s, box-shadow .12s; }
                    .form-pane .fi:focus { border-color: var(--brand-accent, #C75A1B); box-shadow: 0 0 0 3px #FBEFE5; }
                `}</style>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}
